import React, { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * Multi-selector de países con buscador y chips.
 * Props:
 *  - paises:   [{ codigo, nombre }, ...]
 *  - selected: string[] (códigos ISO-2)
 *  - onChange: (nuevo:string[]) => void
 */
export const PaisesSelector = ({ paises, selected, onChange }) => {
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return paises;
    return paises.filter(
      (p) => p.nombre.toLowerCase().includes(qq) || p.codigo.toLowerCase().includes(qq),
    );
  }, [q, paises]);

  const toggle = (code) => {
    if (selected.includes(code)) {
      onChange(selected.filter((x) => x !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  const labelOf = (code) => {
    const p = paises.find((x) => x.codigo === code);
    return p ? `${p.nombre} (${p.codigo})` : code;
  };

  return (
    <div className="border border-ink-200 rounded-lg bg-white overflow-hidden" data-testid="paises-selector">
      <div className="px-3 py-2 border-b border-ink-200 bg-ink-50 flex items-center gap-2">
        <Search className="w-4 h-4 text-ink-400" />
        <input
          type="text"
          placeholder="Buscar país (nombre o código ISO)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-400 text-ink-900"
          data-testid="paises-search"
        />
        <span className="text-xs text-ink-500 font-medium">
          {selected.length} seleccionado{selected.length !== 1 ? 's' : ''}
        </span>
      </div>

      {selected.length > 0 && (
        <div className="px-3 py-2 border-b border-ink-200 bg-brand-50/50 flex flex-wrap gap-1.5">
          {selected.map((code) => (
            <Badge
              key={code}
              className="bg-white border border-brand-200 text-brand-700 hover:bg-brand-100 cursor-pointer"
              onClick={() => toggle(code)}
              data-testid={`pais-chip-${code}`}
            >
              {labelOf(code)} ✕
            </Badge>
          ))}
        </div>
      )}

      <div className="max-h-56 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 p-2">
        {filtered.map((p) => {
          const checked = selected.includes(p.codigo);
          return (
            <button
              type="button"
              key={p.codigo}
              onClick={() => toggle(p.codigo)}
              className={`text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                checked ? 'bg-brand-50 text-brand-900 font-medium' : 'text-ink-700 hover:bg-ink-100'
              }`}
              data-testid={`pais-opt-${p.codigo}`}
            >
              <span
                className={`inline-flex w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                  checked ? 'bg-brand-500 border-brand-500 text-white' : 'border-ink-300'
                }`}
              >
                {checked && <Check className="w-3 h-3" strokeWidth={3} />}
              </span>
              <span className="truncate">{p.nombre}</span>
              <span className="text-[10px] text-ink-400 ml-auto">{p.codigo}</span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-sm text-ink-400 py-4">Sin resultados</p>
        )}
      </div>
    </div>
  );
};

export default PaisesSelector;
